import PropTypes from "prop-types";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "antd";
import { ChipRoot } from "./StatusChipStyled";

/**
 * Semantic pipeline status chip.
 *
 * Accepts either an explicit `variant` (ready / processing / review / failed)
 * or a raw `pipelineStatus` string coming from the backend — in which case
 * it's mapped to a semantic variant. If no status data is available the
 * chip renders nothing, so it's safe to drop into tables where some
 * records pre-date the AI pipeline.
 *
 * Clicking a `review` chip routes the user to `/review`.
 */

const PROCESSING_STATES = new Set([
  "queued",
  "extracting",
  "categorizing",
  "graphing",
  "embedding",
]);

const PROCESSING_STAGE_LABELS = {
  queued: "Queued",
  extracting: "Extracting",
  categorizing: "Categorizing",
  graphing: "Building graph",
  embedding: "Embedding",
};

/**
 * Map a raw backend pipeline_status string to a chip variant.
 * Returns null when the status is empty / unknown so the chip hides.
 */
export function getStatusVariant(pipelineStatus) {
  if (!pipelineStatus) return null;
  const s = String(pipelineStatus).toLowerCase();
  if (s === "completed") return "ready";
  if (s === "review") return "review";
  if (s === "failed") return "failed";
  if (PROCESSING_STATES.has(s)) return "processing";
  return null;
}

const LABELS = {
  ready: "Ready",
  processing: "Processing",
  review: "Needs Review",
  failed: "Failed",
  open: "Open",
  closed: "Closed",
};

const StatusChip = ({
  variant,
  pipelineStatus,
  stageLabel,
  onClick,
  interactive,
  size = "sm",
  className,
}) => {
  const navigate = useNavigate();

  const resolvedVariant = useMemo(() => {
    if (variant) return variant;
    return getStatusVariant(pipelineStatus);
  }, [variant, pipelineStatus]);

  if (!resolvedVariant) return null;

  const resolvedStage =
    stageLabel ||
    (resolvedVariant === "processing" && pipelineStatus
      ? PROCESSING_STAGE_LABELS[String(pipelineStatus).toLowerCase()]
      : null);

  const label = LABELS[resolvedVariant];

  const handleClick = (event) => {
    event.stopPropagation();
    if (onClick) {
      onClick(event);
      return;
    }
    if (resolvedVariant === "review") {
      navigate("/review");
    }
  };

  const isInteractive =
    typeof interactive === "boolean"
      ? interactive
      : Boolean(onClick) || resolvedVariant === "review";

  const chip = (
    <ChipRoot
      as={isInteractive ? "button" : "span"}
      type={isInteractive ? "button" : undefined}
      $variant={resolvedVariant}
      $size={size}
      $interactive={isInteractive}
      onClick={isInteractive ? handleClick : undefined}
      className={className}
      aria-label={resolvedStage ? `${label} — ${resolvedStage}` : label}
    >
      {label}
    </ChipRoot>
  );

  if (resolvedVariant === "processing" && resolvedStage) {
    return (
      <Tooltip title={`${resolvedStage}…`} placement="top">
        {chip}
      </Tooltip>
    );
  }

  if (resolvedVariant === "failed") {
    return (
      <Tooltip title="Extraction failed — click the row to retry" placement="top">
        {chip}
      </Tooltip>
    );
  }

  if (resolvedVariant === "review") {
    return (
      <Tooltip title="Open review queue" placement="top">
        {chip}
      </Tooltip>
    );
  }

  return chip;
};

StatusChip.propTypes = {
  variant: PropTypes.oneOf([
    "ready",
    "processing",
    "review",
    "failed",
    "open",
    "closed",
  ]),
  pipelineStatus: PropTypes.string,
  stageLabel: PropTypes.string,
  onClick: PropTypes.func,
  interactive: PropTypes.bool,
  size: PropTypes.oneOf(["sm", "md"]),
  className: PropTypes.string,
};

export default StatusChip;
